import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  VaAlert,
  VaModal,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import SchemaForm from 'platform/forms-system/src/js/components/SchemaForm';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import { focusElement } from 'platform/utilities/ui';

/**
 * Caps how long a list gets in a modal.
 *
 * Lists of `max` or fewer are shown in full. Longer ones are cut to
 * `max - 1` items plus a summary of the rest, so the list never exceeds
 * `max` lines — for example five medical centers render as three names and
 * "2 other medical centers".
 *
 * @param {string[]} items
 * @param {(count: number) => string} [overflowLabel] omit to never shorten
 * @param {number} [max] most lines to render, summary line included
 * @returns {string[]}
 */
export const truncateItems = (items, overflowLabel, max = 4) => {
  if (!overflowLabel || items.length <= max) {
    return items;
  }
  const shown = items.slice(0, max - 1);
  return [...shown, overflowLabel(items.length - shown.length)];
};

/**
 * Factory for a decision-page `CustomPage` that intercepts Continue with a
 * destructive confirmation modal when the user selects "No" after having
 * already provided evidence gated behind "Yes".
 *
 * Modeled on the v1's /all-claims/components/EvidenceRequestPage.jsx
 *
 * @param {object} config
 * @param {(formData) => boolean} config.shouldConfirm true when "No" is
 *   selected AND there is evidence to remove
 * @param {(formData) => Array<{
 *   intro: React.ReactNode,
 *   items: string[],
 *   overflowLabel?: (count: number) => string,
 * }>} config.getItemGroups groups of item names enumerated in the modal. A
 *   group with an `overflowLabel` has its list shortened (see
 *   `truncateItems`); without one, every item is listed.
 * @param {(formData) => object} config.removeItems returns formData with the
 *   corresponding items removed
 * @param {(formData) => object} config.revertSelection returns formData with
 *   the gating question set back to "Yes" (used when the modal is dismissed)
 * @param {string} config.modalTitle
 * @param {React.ReactNode | ((formData) => React.ReactNode)} [config.modalIntro]
 *   shown above the groups, for wording that applies to all of them
 * @param {string | ((formData) => string)} config.primaryButtonText as a
 *   function, it's passed the form data, so the label can match what's
 *   actually being taken out (e.g. "delete" vs "remove")
 * @param {string} config.secondaryButtonText
 * @param {string | ((formData) => React.ReactNode)} config.alertText success
 *   alert shown after removal. As a function, it's passed the form data as it
 *   was *before* removal, so the wording can reflect what was taken out.
 */
export const confirmEvidenceRemovalPage = ({
  shouldConfirm,
  getItemGroups,
  removeItems,
  revertSelection,
  modalTitle,
  modalIntro,
  primaryButtonText,
  secondaryButtonText,
  alertText,
}) => {
  const ConfirmEvidenceRemovalPage = ({
    name,
    title,
    data,
    schema,
    uiSchema,
    appStateData,
    formContext,
    goBack,
    goForward,
    setFormData,
    contentBeforeButtons,
    contentAfterButtons,
  }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    // Worked out at confirm time, while the removed evidence is still in
    // `data`.
    const [alertMessage, setAlertMessage] = useState(null);

    const handlers = {
      onSubmit: ({ formData }) => {
        if (shouldConfirm(formData)) {
          setModalVisible(true);
        } else {
          setAlertVisible(false);
          goForward({ formData });
        }
      },
      onConfirmRemove: () => {
        setAlertMessage(
          typeof alertText === 'function' ? alertText(data) : alertText,
        );
        setFormData(removeItems(data));
        setModalVisible(false);
        /**
          thread for reason to use setTimeout here
          @see https://dsva.slack.com/archives/C01DBGX4P45/p1774282809644219
        */
        setTimeout(() => {
          // focus on the 'continue' button
          focusElement('.usa-button-primary');
          // followed by setting the alert to be visible
          setAlertVisible(true);
        }, 100);
      },
      onCancelChange: () => {
        setFormData(revertSelection(data));
        setModalVisible(false);
      },
    };

    const itemGroups = modalVisible ? getItemGroups(data) : [];
    const intro =
      typeof modalIntro === 'function' ? modalIntro(data) : modalIntro;
    const primaryLabel =
      typeof primaryButtonText === 'function'
        ? primaryButtonText(data)
        : primaryButtonText;

    return (
      <>
        {alertVisible && (
          <div className="vads-u-margin-bottom--1">
            <VaAlert
              role="alert"
              closeBtnAriaLabel="Close notification"
              closeable
              onCloseEvent={() => setAlertVisible(false)}
              fullWidth="false"
              slim
              status="success"
              uswds
            >
              <p className="vads-u-margin-y--0">{alertMessage}</p>
            </VaAlert>
          </div>
        )}
        <VaModal
          clickToClose
          modalTitle={modalTitle}
          onCloseEvent={handlers.onCancelChange}
          onPrimaryButtonClick={handlers.onConfirmRemove}
          onSecondaryButtonClick={handlers.onCancelChange}
          visible={modalVisible}
          status="warning"
          primaryButtonText={primaryLabel}
          secondaryButtonText={secondaryButtonText}
        >
          {intro}
          {itemGroups
            .filter(group => group.items.length)
            .map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {group.intro}
                <ul>
                  {truncateItems(group.items, group.overflowLabel).map(
                    (item, itemIndex) => (
                      <li key={itemIndex}>
                        <strong>{item}</strong>
                      </li>
                    ),
                  )}
                </ul>
              </React.Fragment>
            ))}
        </VaModal>
        <SchemaForm
          name={name}
          title={title}
          data={data}
          schema={schema}
          uiSchema={uiSchema}
          appStateData={appStateData}
          formContext={formContext}
          onChange={setFormData}
          onSubmit={handlers.onSubmit}
        >
          {contentBeforeButtons}
          <FormNavButtons
            goBack={goBack}
            submitToContinue
            useWebComponents
            enableAnalytics
          />
          {contentAfterButtons}
        </SchemaForm>
      </>
    );
  };

  ConfirmEvidenceRemovalPage.propTypes = {
    appStateData: PropTypes.object,
    contentAfterButtons: PropTypes.element,
    contentBeforeButtons: PropTypes.element,
    data: PropTypes.object,
    formContext: PropTypes.object,
    goBack: PropTypes.func,
    goForward: PropTypes.func,
    name: PropTypes.string,
    schema: PropTypes.object,
    setFormData: PropTypes.func,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    uiSchema: PropTypes.object,
  };

  return ConfirmEvidenceRemovalPage;
};

export default confirmEvidenceRemovalPage;
